import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { PubService, Publicite } from './services/Pub.service';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    SidebarComponent,
    HttpClientModule
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'EDUCANET';
  isAuthenticated = false;
  showNavbarFooter = true;
  isSidebarOpen: boolean = true; // Default to open to match sidebar component
  private platformId: any;

  // Banner web ad (top of all pages)
  bannerAd?: Publicite;
  // Under footer ad (bottom of all pages)
  underFooterAd?: Publicite;

  constructor(private router: Router, private pubService: PubService, private http: HttpClient, private authService: AuthService, @Inject(PLATFORM_ID) platformId: Object) {
    this.platformId = platformId; // store for later use
    // Update auth state on route changes (e.g., after login navigation)
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateAuthState();
        // Save current URL for refresh handling
        if (event.urlAfterRedirects && !event.urlAfterRedirects.includes('/home')) {
          this.authService.saveCurrentUrl(event.urlAfterRedirects);
        }
      });
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorageChange);
    }

    // If credentials were moved to sessionStorage (by index.html), attempt auto-login and ensure they're cleared
    try {
      if (isPlatformBrowser(this.platformId)) {
        const pending = sessionStorage.getItem('pendingCreds');
        const creds = pending ? JSON.parse(pending) : null;
        const email = creds?.email;
        const password = creds?.password;

        if (email && password) {
          const url = 'https://.educanet.pro/api/user/UserByLoginAndPassword';
          const body = { login: email.trim(), password };

          this.http.post<any>(url, body, { headers: { 'Content-Type': 'application/json' } })
            .subscribe({
              next: (res) => {
                // Clear pending creds regardless of outcome to ensure they never remain
                try { sessionStorage.removeItem('pendingCreds'); } catch (e) { }

                if (res?.response?.iduser) {
                  // Use AuthService for login
                  this.authService.login(res.response);

                  try {
                    localStorage.setItem('showVideoModal', 'true');
                  } catch (e) {
                    console.warn('Erreur lors de la sauvegarde localStorage:', e);
                  }

                  // Ensure any leftover email/password query params are removed (extra safety)
                  try {
                    const urlObj = new URL(window.location.href);
                    urlObj.searchParams.delete('email');
                    urlObj.searchParams.delete('password');
                    history.replaceState({}, '', urlObj.pathname + urlObj.search);

                    // Add a popstate listener to prevent returning to credentialed URL
                    window.addEventListener('popstate', () => {
                      const u = new URL(window.location.href);
                      if (u.searchParams.has('email') || u.searchParams.has('password')) {
                        u.searchParams.delete('email');
                        u.searchParams.delete('password');
                        history.replaceState({}, '', u.pathname + u.search);
                      }
                    });

                    this.updateAuthState();
                  } catch (e) {
                    // ignore URL manipulation errors
                  }
                }
              },
              error: () => {
                // Clear pending creds and remove any query params
                try { sessionStorage.removeItem('pendingCreds'); } catch (e) { }
                try {
                  const urlObj = new URL(window.location.href);
                  urlObj.searchParams.delete('email');
                  urlObj.searchParams.delete('password');
                  history.replaceState({}, '', urlObj.pathname + urlObj.search);
                } catch (e) { }
              }
            });
        }
      }
    } catch (e) {
      // ignore
    }

    // Load BannerWeb ad (1/1)
    this.pubService
      .getPublicites('Pubeducanet', 'BannerWeb', 1, 1)
      .subscribe({
        next: (res: any) => {
          const list: Publicite[] = res?.results?.articlePublicitaire || res || [];
          this.bannerAd = list[0];
        },
        error: () => {
          this.bannerAd = undefined;
        },
      });
    // Load UnderFooterWeb ad (1/1)
    this.pubService
      .getPublicites('Pubeducanet', 'UnderFooterWeb', 1, 1)
      .subscribe({
        next: (res: any) => {

          const list: Publicite[] = res?.results?.articlePublicitaire || res || [];
          this.underFooterAd = list[0];
        },
        error: () => {
          this.underFooterAd = undefined;
        },
      });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorageChange);
    }
  }

  private onStorageChange = (event: StorageEvent) => {
    if (event.key === 'isLoggedIn') {
      this.updateAuthState();
    }
  };

  private updateAuthState(): void {
    try {
      this.isAuthenticated = this.authService.isAuthenticated();
      // Hide navbar and footer on the home page
      this.showNavbarFooter =
        this.router.url !== '/home' &&
        this.router.url !== '/' &&
        this.router.url !== '/contact';
      // Set sidebar to open by default when authenticated and navbar/footer are shown
      this.isSidebarOpen = this.isAuthenticated && this.showNavbarFooter;
    } catch {
      this.isAuthenticated = false;
      this.showNavbarFooter = true;
      this.isSidebarOpen = false;
    }
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  onSidebarToggle(isOpen: boolean) {
    this.isSidebarOpen = isOpen;
  }
}
